import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { builderSurfaceJsonContentHeader } from '../common/utils/restApiHeaderUtils';
import { Input, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import {
  appSettingsURL,
  createPagesURL,
  originURLMapping,
  typeIcons,
  types,
} from './shortcutUtils';
import { toast } from 'react-toastify';
import { getCookie, isProjectReadOnly } from '../features/home/app_util';

const Shortcut = ({ projectId, version }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);
  const [suggestion, setSuggestion] = useState('');

  const searchContainerRef = useRef(null);
  const inputRef = useRef(null);

  let token = getCookie('token');
  if (!token) {
    token = '';
  }

  useEffect(() => {
    if (isVisible) {
      setTimeout(() => {
        inputRef.current.focus();
      }, 100);
    }
  }, [isVisible]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.code === 'Space') {
        e.preventDefault();
        e.stopImmediatePropagation();
        setIsVisible((prev) => {
          if (!prev) {
            setQuery('');
            setSuggestion('');
          }
          return !prev;
        });
      }

      if (e.key === 'Escape') {
        setIsVisible(false);
        setQuery('');
        setSuggestion('');
      }
    };

    const handleClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setIsVisible(false);
        setQuery('');
        setSuggestion('');
      }
    };

    const handleToggleSearch = (e) => {
      setIsVisible(e.detail.visible);
      setQuery('');
      setSuggestion('');
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('toggleGlobalSearch', handleToggleSearch);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('toggleGlobalSearch', handleToggleSearch);
    };
  }, []);

  useEffect(() => {
    if (query.trim() === '') {
      setResults([]);
      return;
    }

    const handleSearch = async () => {
      try {
        const header = builderSurfaceJsonContentHeader(projectId);
        const response = await axios.post(
          `${process.env.REACT_APP_BUILDER_ENGINE}typesense/search/${process.env.REACT_APP_TYPESENSE_COLLECTION_NAME}`,
          { query },
          header,
        );
        if (response.data && Array.isArray(response.data)) {
          setResults(response.data);
        } else {
          setResults([]);
        }
        setError(null);
      } catch (err) {
        console.error('Search error:', err);
        setError(err.response.data.error || 'An error occurred while searching');
        setResults([]);
      }
    };
    handleSearch();
  }, [query]);

  const groupResultsByType = (results) => {
    return results.reduce((acc, result) => {
      if (!acc[result.type]) {
        acc[result.type] = [];
      }
      acc[result.type].push(result);
      return acc;
    }, {});
  };

  const groupedResults = groupResultsByType(results);
  const appSettingsURLs = appSettingsURL(version, projectId);
  const createPagesURLs = createPagesURL(version, projectId);
  const matchingAppSettings = Object.keys(appSettingsURLs).filter((setting) =>
    setting.toLowerCase().includes(query.trim().toLowerCase()),
  );

  const renderAnchorTag = (type, result) => {
    const redirectUrl = `${originURLMapping(type)}${result.url || ''}?token=${token}`;
    return (
      <a href={redirectUrl} className="result-item">
        <span className="icon me-2 mt-2 ml-2">{typeIcons[type]}</span>
        <h6 className="search-name mb-0">{result.name}</h6>
      </a>
    );
  };

  const reindexingData = async () => {
    try {
      toast.info('Started Reindexing the Data.');
      const header = builderSurfaceJsonContentHeader(projectId);
      const response = await axios.post(
        `${process.env.REACT_APP_BUILDER_ENGINE}typesense/re-indexing-data`,
        {},
        header,
      );
      if (response.data.success) {
        toast.success('Data reindexed successfully.');
      } else {
        toast.error('Reindexing failed. Please try again.');
        console.error('Reindexing failed:', response);
      }
    } catch (error) {
      console.error('Error during reindexing:', error);
      toast.error('An error occurred while reindexing the data. Please try again.');
    }
  };

  const handleInputChange = (e) => {
    const input = e.target.value;
    setQuery(input);
    const match = types.find((word) => word.toLowerCase().startsWith(input.toLowerCase()));
    setSuggestion(match && input.length > 0 ? match : '');
  };

  const handleTabKeyDown = (e) => {
    if (e.key === 'Tab' && suggestion) {
      e.preventDefault();
      setQuery(`${suggestion}:`);
      setSuggestion('');
    }
  };

  const isReadOnly = isProjectReadOnly(projectId);

  return (
    <>
      <Modal
        isOpen={isVisible}
        toggle={() => setIsVisible(false)}
        size="lg"
        className="top-position-modal"
      >
        <ModalHeader
          toggle={() => setIsVisible(false)}
          className="d-flex align-items-center justify-content-between"
        >
          <div className="d-flex align-items-center">
            <h5 className="search-modal-header">Shortcut</h5>
            <button
              type="button"
              className="btn btn-theme-cancel btn-xs search-reindex-button"
              title="Reindex Data"
              onClick={reindexingData}
            >
              <i className="wb webconnect-refresh"></i>
            </button>
          </div>
        </ModalHeader>
        <ModalBody>
          <Input
            type="text"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleTabKeyDown}
            placeholder="Search for something..."
            className="mb-3"
            innerRef={inputRef}
          />
          {suggestion && query !== suggestion && (
            <small className="text-muted">
              Did you mean: <strong>{suggestion}</strong>? (Press <kbd>Tab</kbd> to autocomplete)
            </small>
          )}
          {error && <p className="text-danger">{error}</p>}
          {results.length === 0 && query.trim() !== '' && !error && (
            <div className="text-center">
              <p className="text-muted">No results found</p>
              {Object.keys(createPagesURLs).map((type) => {
                const lowerQuery = query.trim().toLowerCase();
                const isTypeMatch = type.toLowerCase().includes(lowerQuery.replace(':', ''));
                if (isTypeMatch || lowerQuery === `${type.toLowerCase()}:`) {
                  return (
                    <a
                      key={type}
                      className="btn btn-theme-submit btn-xs m-1 search-create"
                      href={isReadOnly ? '#' : createPagesURLs[type]}
                      onClick={(e) => {
                        if (isReadOnly) {
                          e.preventDefault();
                        }
                      }}
                      style={{
                        cursor: isReadOnly ? 'not-allowed' : 'pointer',
                        opacity: isReadOnly ? 0.5 : 1,
                      }}
                    >
                      <i className="wb webconnect-plus"></i>&nbsp;{type}
                    </a>
                  );
                }
                return null;
              })}
            </div>
          )}
          {results.length > 0 && (
            <div>
              {Object.keys(groupedResults).map((type) => (
                <div key={type} className="result-group">
                  <h5 className="group-title">{type}</h5>
                  <ul className="list-unstyled">
                    {groupedResults[type].map((result, index) => (
                      <li key={index} className="search-item">
                        {renderAnchorTag(type, result)}
                      </li>
                    ))}
                  </ul>
                  <a
                    className="btn btn-theme-submit btn-xs search-create"
                    href={isReadOnly ? '#' : createPagesURLs[type]}
                    onClick={(e) => {
                      if (isReadOnly) {
                        e.preventDefault();
                      }
                    }}
                    style={{
                      cursor: isReadOnly ? 'not-allowed' : 'pointer',
                      opacity: isReadOnly ? 0.5 : 1,
                    }}
                  >
                    <i className="wb webconnect-plus"></i>&nbsp;{type}
                  </a>
                </div>
              ))}
            </div>
          )}
          {query.trim() !== '' && matchingAppSettings.length > 0 && (
            <div className="mt-4">
              <h5 className="group-title">Go To:</h5>
              <ul className="list-unstyled">
                {matchingAppSettings.map((setting) => (
                  <li key={setting} className="search-item">
                    <a href={appSettingsURLs[setting]} className="result-item">
                      <h6 className="search-name mb-0">Go To: {setting}</h6>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {query.toLowerCase().includes('cre') && (
            <div className="mt-4">
              <h5 className="group-title">Create New:</h5>
              <div className="d-flex flex-wrap">
                {Object.keys(createPagesURLs).map((type) => (
                  <a
                    key={type}
                    className="btn btn-theme-submit btn-xs m-1 search-create"
                    href={isReadOnly ? '#' : createPagesURLs[type]}
                    onClick={(e) => {
                      if (isReadOnly) {
                        e.preventDefault();
                      }
                    }}
                    style={{
                      cursor: isReadOnly ? 'not-allowed' : 'pointer',
                      opacity: isReadOnly ? 0.5 : 1,
                    }}
                  >
                    <i className="wb webconnect-plus"></i>&nbsp;{type}
                  </a>
                ))}
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between">
          <span className="search-modal-footer">
            Press <strong>CTRL + Space</strong> to open from anywhere
          </span>
          <span className="search-modal-footer">
            Press <strong>ESC</strong> or click anywhere to close
          </span>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default Shortcut;
